import { Pie } from "react-chartjs-2";
import { useMemo } from "react";
import LoadingSpinner from "../LoadingSpinner";
export function PieChartComponent({
  isMobile,
  failedMessagesCount,
  failedMessagesData,
  fetchingFailedMessages,
}) {
  function wrapLines(str, maxLen = 40) {
    if (!str) return [];
    const words = str.split(" ");
    const lines = [];
    let currentLine = "";
    for (const word of words) {
      if ((currentLine + " " + word).trim().length > maxLen) {
        lines.push(currentLine.trim());
        currentLine = word;
      } else {
        currentLine += " " + word;
      }
    }
    if (currentLine) lines.push(currentLine.trim());
    return lines;
  }

  function PieLegend({
    labels,
    backgroundColors,
    isMobile = false,
    className = "",
    style = {},
  }) {
    return (
      <div
        className={`${
          isMobile ? "mb-4 w-full" : "max-h-[400px] overflow-y-auto w-[40%]"
        } ${className}`}
        style={{
          ...(isMobile
            ? {}
            : { maxHeight: 400, minWidth: 180, overflowY: "auto" }),
          ...style,
        }}
      >
        {labels.map((label, i) => (
          <div key={i} className="flex items-center mb-2">
            <span
              style={{
                display: "inline-block",
                width: 12,
                height: 12,
                backgroundColor: backgroundColors[i % backgroundColors.length],
                marginRight: 8,
                borderRadius: 2,
              }}
            />
            <span className="text-xs w-[90%] pr-2">
              {Array.isArray(label) ? label.join(" ") : label}
            </span>
          </div>
        ))}
      </div>
    );
  }

  const processedData = useMemo(() => {
    if (!failedMessagesData || !Array.isArray(failedMessagesData)) {
      return {
        labels: [],
        displayLabels: [],
        dataChart: [],
        failedReasonFullText: {},
      };
    }

    const failedReasonCounts = {};
    const failedReasonFullText = {};
    let totalFromView = 0;

    for (const item of failedMessagesData) {
      const reason = item.failedReason?.split(":")[0] ?? "Unknown";
      const count = item.bucket || 0;
      failedReasonCounts[reason] = (failedReasonCounts[reason] || 0) + count;
      totalFromView += count;
      if (!failedReasonFullText[reason]) {
        failedReasonFullText[reason] = item.failedReason ?? "Unknown";
      }
    }

    if (failedMessagesCount > totalFromView) {
      const unknownCount = failedMessagesCount - totalFromView;
      failedReasonCounts["Unknown"] = unknownCount;
      failedReasonFullText["Unknown"] = "Unknown";
    }

    const labels = Object.keys(failedReasonFullText);
    const displayLabels = labels.map((label) => wrapLines(label, 30));
    const dataChart = Object.values(failedReasonCounts);

    return {
      labels,
      displayLabels,
      dataChart,
      failedReasonFullText,
    };
  }, [failedMessagesData, failedMessagesCount]);

  const { labels, displayLabels, dataChart, failedReasonFullText } =
    processedData;

  const backgroundColors = [
    "#FAC70C",
    "#F6A81A",
    "#F18A29",
    "#ED6B38",
    "#E84C46",
    "#E42E55",
    "#DF0F63",
    "#D31062",
    "#BC115F",
    "#A4125C",
    "#8C1359",
    "#751456",
    "#5D1553",
    "#451650",
    "#22184B",
  ];

  const dataPie = {
    labels: displayLabels,
    datasets: [
      {
        label: "Failed reasons",
        data: dataChart,
        backgroundColor: backgroundColors,
        borderColor: "white",
        borderWidth: 3,
        hoverOffset: 4,
        rotation: 90,
      },
    ],
  };

  const options = {
    responsive: true,
    plugins: {
      legend: {
        position: isMobile ? "top" : "right",
        align: "start",
      },
      tooltip: {
        backgroundColor: "#FAFAFA",
        borderColor: "#52525b",
        borderWidth: 0.9,
        titleColor: "#000000",
        bodyColor: "#000000",
        cornerRadius: 10,
        style: { width: "20px" },
        callbacks: {
          label: function (tooltipItem) {
            const dataset = tooltipItem.dataset;
            const total = dataset.data.reduce((sum, value) => sum + value, 0);
            const currentValue = dataset.data[tooltipItem.dataIndex];
            const percentage = Math.round((currentValue / total) * 100);
            let label = tooltipItem.chart.data.labels[tooltipItem.dataIndex];
            if (Array.isArray(label)) {
              label = label.join(" ");
            }

            const fullReason = failedReasonFullText[label];

            let reasonDetails = [];
            if (fullReason && fullReason.includes(":")) {
              reasonDetails = fullReason.split(":").slice(1).join(":").trim();
            }
            const wrappedDetails =
              reasonDetails.length > 0
                ? wrapLines(reasonDetails, 40)
                : ["No details"];

            return [...wrappedDetails, `${currentValue} (${percentage}%)`];
          },
        },
      },
    },
    layout: {
      autoPadding: true,
    },
    ...(isMobile ? {} : { aspectRatio: 1.4 }),
    maintainAspectRatio: true,
  };

  if (fetchingFailedMessages) {
    return (
      <div className="mx-auto">
        <div className="flex items-center align-middle">
          <div className="text-lg font-bold">Failed reasons</div>
          {failedMessagesCount && (
            <div className="text-sm ml-2">{`(${failedMessagesCount} failed messages)`}</div>
          )}
        </div>
        <LoadingSpinner />;
      </div>
    );
  }

  if (!failedMessagesCount) {
    return (
      <div className="mx-auto">
        <div className="text-lg font-bold">Failed reasons</div>
        <div className="mx-auto text-center text-gray-500 py-8">
          No failed messages for this period.
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto">
      <div className="flex items-center align-middle">
        <div className="text-lg font-bold">Failed reasons</div>
        {failedMessagesCount && (
          <div className="text-sm ml-2">{`(${failedMessagesCount} failed messages)`}</div>
        )}
      </div>
      <div className="mx-auto mt-3">
        <div
          className={`flex ${isMobile ? "flex-col" : "flex-row"} items-between w-full`}
        >
          <div className="h-full mt-5 flex-1 w-[59%]">
            <Pie
              data={dataPie}
              options={{
                ...options,
                plugins: { ...options.plugins, legend: { display: false } },
              }}
            />
          </div>
          <PieLegend
            labels={labels}
            backgroundColors={backgroundColors}
            isMobile={isMobile}
          />
        </div>
      </div>
    </div>
  );
}
